import React, { useState, useEffect, useRef, useCallback } from 'react';
import styled from 'styled-components';
import {
  FaPlus, FaEdit, FaTrash, FaSave, FaTimes, FaFileAlt,
  FaEye, FaSpinner, FaSms
} from 'react-icons/fa';
import DashboardLayout from '../../../layouts/DashboardLayout';
import Modal from '../../../components/shared/Modal';
import ModalNotification from '../../../components/shared/ModalNotification';
import VariableInserter from '../components/VariableInserter';
import { replaceWithSampleData } from '../utils/templateUtils';
import {
  getTemplates,
  createTemplate,
  updateTemplate,
  deleteTemplate,
} from '../services/followUpService';

// ── Styled Components ──

const PageHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${p => p.theme.spacing(2)};
  margin-bottom: ${p => p.theme.spacing(3)};

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;

  h1 {
    color: ${p => p.theme.colors.primary};
    margin: 0;
    font-size: 1.4rem;
  }

  svg {
    color: ${p => p.theme.colors.primary};
    font-size: 1.3rem;
  }
`;

const CreateBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 22px;
  background: ${p => p.theme.colors.primary};
  color: #000;
  border: none;
  border-radius: 24px;
  font-weight: 600;
  font-size: 0.88rem;
  cursor: pointer;
  white-space: nowrap;
  transition: opacity 0.15s;
  &:hover { opacity: 0.9; }

  @media (max-width: 768px) { width: 100%; justify-content: center; }
`;

// ── Form (Inline) ──

const FormCard = styled.div`
  background: ${p => p.theme.colors.surface};
  border-radius: 12px;
  padding: ${p => p.theme.spacing(3)};
  margin-bottom: ${p => p.theme.spacing(3)};
  border: 1.5px solid ${p => p.theme.colors.primary}33;
  animation: slideDown 0.2s ease;

  @keyframes slideDown {
    from { opacity: 0; transform: translateY(-8px); }
    to { opacity: 1; transform: translateY(0); }
  }
`;

const FormTitle = styled.h2`
  color: ${p => p.theme.colors.textPrimary};
  margin: 0 0 ${p => p.theme.spacing(2.5)} 0;
  font-size: 1.1rem;
  font-weight: 600;
`;

const FieldGroup = styled.div`
  margin-bottom: ${p => p.theme.spacing(2)};
`;

const FieldLabel = styled.label`
  display: block;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: ${p => p.theme.colors.textSecondary};
  margin-bottom: 6px;
`;

const TextInput = styled.input`
  width: 100%;
  padding: 10px 14px;
  background: ${p => p.theme.colors.background};
  border: 1.5px solid ${p => p.theme.colors.inputGray}44;
  border-radius: ${p => p.theme.borderRadius};
  color: ${p => p.theme.colors.textPrimary};
  font-size: 0.9rem;
  transition: border-color 0.2s;

  &:focus {
    outline: none;
    border-color: ${p => p.theme.colors.primary};
  }
`;

const SelectInput = styled.select`
  width: 100%;
  padding: 10px 14px;
  background: ${p => p.theme.colors.background};
  border: 1.5px solid ${p => p.theme.colors.inputGray}44;
  border-radius: ${p => p.theme.borderRadius};
  color: ${p => p.theme.colors.textPrimary};
  font-size: 0.9rem;

  &:focus {
    outline: none;
    border-color: ${p => p.theme.colors.primary};
  }

  option {
    background: ${p => p.theme.colors.background};
    color: ${p => p.theme.colors.textPrimary};
  }
`;

const BodyTextarea = styled.textarea`
  width: 100%;
  padding: 12px 14px;
  background: ${p => p.theme.colors.background};
  border: 1.5px solid ${p => p.theme.colors.inputGray}44;
  border-radius: ${p => p.theme.borderRadius};
  color: ${p => p.theme.colors.textPrimary};
  font-size: 0.9rem;
  min-height: 120px;
  resize: vertical;
  font-family: inherit;
  line-height: 1.5;
  transition: border-color 0.2s;

  &:focus {
    outline: none;
    border-color: ${p => p.theme.colors.primary};
  }

  &::placeholder {
    color: ${p => p.theme.colors.inputGray};
  }
`;

const CharCount = styled.div`
  text-align: right;
  font-size: 0.78rem;
  color: ${p => p.count > 240 ? p.theme.colors.error : p.theme.colors.textSecondary};
  margin-top: 4px;
`;

const PreviewBox = styled.div`
  background: ${p => p.theme.colors.background};
  border: 1px dashed ${p => p.theme.colors.inputGray}55;
  border-radius: ${p => p.theme.borderRadius};
  padding: 14px;
  margin-top: ${p => p.theme.spacing(1)};

  .preview-label {
    font-size: 0.72rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: ${p => p.theme.colors.textSecondary};
    margin-bottom: 8px;
    display: flex;
    align-items: center;
    gap: 6px;
  }

  .preview-text {
    color: ${p => p.theme.colors.textPrimary};
    font-size: 0.88rem;
    line-height: 1.5;
    white-space: pre-wrap;
    word-wrap: break-word;
  }

  .preview-empty {
    color: ${p => p.theme.colors.inputGray};
    font-style: italic;
    font-size: 0.85rem;
  }
`;

const FormActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${p => p.theme.spacing(1.5)};
  margin-top: ${p => p.theme.spacing(2.5)};
`;

const SaveBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 22px;
  background: ${p => p.theme.colors.primary};
  color: #000;
  border: none;
  border-radius: 24px;
  font-weight: 600;
  font-size: 0.86rem;
  cursor: pointer;
  transition: opacity 0.15s;
  &:hover:not(:disabled) { opacity: 0.9; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const CancelBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 22px;
  background: transparent;
  color: ${p => p.theme.colors.textSecondary};
  border: 1.5px solid ${p => p.theme.colors.inputGray}44;
  border-radius: 24px;
  font-weight: 500;
  font-size: 0.86rem;
  cursor: pointer;
  &:hover { border-color: ${p => p.theme.colors.textPrimary}; color: ${p => p.theme.colors.textPrimary}; }
`;

// ── Template List ──

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(340px, 1fr));
  gap: ${p => p.theme.spacing(2)};

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const TemplateCard = styled.div`
  background: ${p => p.theme.colors.surface};
  border-radius: 12px;
  padding: ${p => p.theme.spacing(2.5)};
  border: 1.5px solid ${p => p.theme.colors.inputGray}22;
  transition: border-color 0.2s, transform 0.15s;

  &:hover {
    border-color: ${p => p.theme.colors.primary}44;
    transform: translateY(-2px);
  }
`;

const CardTop = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 10px;
`;

const CardName = styled.div`
  font-size: 0.95rem;
  font-weight: 600;
  color: ${p => p.theme.colors.textPrimary};
`;

const CategoryBadge = styled.span`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 0.7rem;
  font-weight: 600;
  background: ${p => {
    switch (p.category) {
      case 'Follow Up': return '#BA68C822';
      case 'Quote': return '#FFB74D22';
      case 'Scheduling': return '#4FC3F722';
      default: return p.theme.colors.primary + '22';
    }
  }};
  color: ${p => {
    switch (p.category) {
      case 'Follow Up': return '#BA68C8';
      case 'Quote': return '#FFB74D';
      case 'Scheduling': return '#4FC3F7';
      default: return p.theme.colors.primary;
    }
  }};
`;

const CardBody = styled.div`
  font-size: 0.84rem;
  color: ${p => p.theme.colors.textSecondary};
  line-height: 1.5;
  margin-bottom: 14px;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  white-space: pre-wrap;
`;

const CardActions = styled.div`
  display: flex;
  gap: 8px;
  border-top: 1px solid ${p => p.theme.colors.inputGray}22;
  padding-top: 10px;
`;

const CardActionBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 6px 12px;
  background: transparent;
  border: 1px solid ${p => p.theme.colors.inputGray}33;
  border-radius: 16px;
  color: ${p => p.theme.colors.textSecondary};
  font-size: 0.76rem;
  cursor: pointer;
  transition: all 0.15s;

  &:hover {
    border-color: ${p => p.$danger ? p.theme.colors.error : p.theme.colors.primary};
    color: ${p => p.$danger ? p.theme.colors.error : p.theme.colors.primary};
    background: ${p => p.$danger ? p.theme.colors.error + '12' : p.theme.colors.primary + '12'};
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${p => p.theme.spacing(6)} ${p => p.theme.spacing(3)};
  color: ${p => p.theme.colors.textSecondary};

  svg {
    font-size: 48px;
    color: ${p => p.theme.colors.inputGray}55;
    margin-bottom: ${p => p.theme.spacing(2)};
  }

  h3 {
    color: ${p => p.theme.colors.textPrimary};
    margin: 0 0 8px 0;
    font-size: 1.1rem;
  }

  p {
    font-size: 0.88rem;
    max-width: 400px;
    margin: 0 auto;
    line-height: 1.5;
  }
`;

const LoadingWrapper = styled.div`
  display: flex;
  justify-content: center;
  padding: ${p => p.theme.spacing(6)};
  color: ${p => p.theme.colors.primary};

  .spinning {
    animation: spin 1s linear infinite;
    font-size: 28px;
  }

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

// ── Categories ──
const CATEGORIES = ['General', 'Follow Up', 'Quote', 'Scheduling'];

const TemplateBuilderPage = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState(null);

  // Form state
  const [formName, setFormName] = useState('');
  const [formBody, setFormBody] = useState('');
  const [formCategory, setFormCategory] = useState('General');

  // Notification
  const [feedback, setFeedback] = useState({ show: false, message: '', success: true });

  const bodyRef = useRef(null);

  const fetchTemplates = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getTemplates();
      setTemplates(data);
    } catch (err) {
      setFeedback({ show: true, message: 'Failed to load templates', success: false });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  const resetForm = () => {
    setFormName('');
    setFormBody('');
    setFormCategory('General');
    setEditingId(null);
    setShowForm(false);
  };

  const handleCreate = () => {
    resetForm();
    setShowForm(true);
  };

  const handleEdit = (template) => {
    setFormName(template.name);
    setFormBody(template.body);
    setFormCategory(template.category || 'General');
    setEditingId(template.id);
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSave = async () => {
    if (!formName.trim() || !formBody.trim()) {
      setFeedback({ show: true, message: 'Name and body are required', success: false });
      return;
    }

    setSaving(true);
    try {
      const data = { name: formName.trim(), body: formBody.trim(), category: formCategory };
      if (editingId) {
        await updateTemplate(editingId, data);
        setFeedback({ show: true, message: 'Template updated successfully', success: true });
      } else {
        await createTemplate(data);
        setFeedback({ show: true, message: 'Template created successfully', success: true });
      }
      resetForm();
      await fetchTemplates();
    } catch (err) {
      setFeedback({ show: true, message: 'Failed to save template', success: false });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!templateToDelete) return;
    try {
      await deleteTemplate(templateToDelete);
      setFeedback({ show: true, message: 'Template deleted', success: true });
      await fetchTemplates();
    } catch (err) {
      setFeedback({ show: true, message: 'Failed to delete template', success: false });
    } finally {
      setDeleteModalOpen(false);
      setTemplateToDelete(null);
    }
  };

  const handleInsertVariable = (variable) => {
    const textarea = bodyRef.current;
    if (!textarea) {
      setFormBody(prev => prev + variable);
      return;
    }
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const newBody = formBody.substring(0, start) + variable + formBody.substring(end);
    setFormBody(newBody);
    setTimeout(() => {
      textarea.focus();
      const newPos = start + variable.length;
      textarea.setSelectionRange(newPos, newPos);
    }, 0);
  };

  const preview = formBody ? replaceWithSampleData(formBody) : '';

  return (
    <DashboardLayout>
      <PageHeader>
        <TitleRow>
          <FaSms />
          <h1>SMS Templates</h1>
        </TitleRow>
        {!showForm && (
          <CreateBtn onClick={handleCreate}>
            <FaPlus size={12} /> Create Template
          </CreateBtn>
        )}
      </PageHeader>

      {/* Inline Form */}
      {showForm && (
        <FormCard>
          <FormTitle>{editingId ? 'Edit Template' : 'Create New Template'}</FormTitle>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <FieldGroup>
              <FieldLabel>Template Name</FieldLabel>
              <TextInput
                value={formName}
                onChange={e => setFormName(e.target.value)}
                placeholder="e.g. Follow Up After Quote"
                autoFocus
              />
            </FieldGroup>
            <FieldGroup>
              <FieldLabel>Category</FieldLabel>
              <SelectInput value={formCategory} onChange={e => setFormCategory(e.target.value)}>
                {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
              </SelectInput>
            </FieldGroup>
          </div>

          <FieldGroup>
            <FieldLabel>Message Body</FieldLabel>
            <BodyTextarea
              ref={bodyRef}
              value={formBody}
              onChange={e => setFormBody(e.target.value)}
              placeholder="Hi {first_name}, this is {company_name}. Your quote is ready: {quote_url}"
            />
            <CharCount count={formBody.length}>{formBody.length} characters</CharCount>
          </FieldGroup>

          <VariableInserter onInsert={handleInsertVariable} />

          {preview && (
            <PreviewBox>
              <div className="preview-label">
                <FaEye size={12} /> Live Preview (sample data)
              </div>
              <div className="preview-text">{preview}</div>
            </PreviewBox>
          )}

          <FormActions>
            <CancelBtn onClick={resetForm}>
              <FaTimes size={12} /> Cancel
            </CancelBtn>
            <SaveBtn onClick={handleSave} disabled={saving || !formName.trim() || !formBody.trim()}>
              {saving ? <FaSpinner className="spinning" /> : <FaSave size={12} />}
              {editingId ? 'Update Template' : 'Save Template'}
            </SaveBtn>
          </FormActions>
        </FormCard>
      )}

      {/* Template List */}
      {loading ? (
        <LoadingWrapper>
          <FaSpinner className="spinning" />
        </LoadingWrapper>
      ) : templates.length === 0 ? (
        <EmptyState>
          <FaFileAlt />
          <h3>No templates yet</h3>
          <p>Create your first SMS template to quickly send personalized messages to your leads.</p>
        </EmptyState>
      ) : (
        <Grid>
          {templates.map(t => (
            <TemplateCard key={t.id}>
              <CardTop>
                <CardName>{t.name}</CardName>
                {t.category && <CategoryBadge category={t.category}>{t.category}</CategoryBadge>}
              </CardTop>
              <CardBody>{t.body}</CardBody>
              <CardActions>
                <CardActionBtn onClick={() => handleEdit(t)}>
                  <FaEdit size={11} /> Edit
                </CardActionBtn>
                <CardActionBtn
                  $danger
                  onClick={() => { setTemplateToDelete(t.id); setDeleteModalOpen(true); }}
                >
                  <FaTrash size={11} /> Delete
                </CardActionBtn>
              </CardActions>
            </TemplateCard>
          ))}
        </Grid>
      )}

      {/* Delete confirmation */}
      <Modal
        title="Delete Template"
        isOpen={deleteModalOpen}
        onCancel={() => { setDeleteModalOpen(false); setTemplateToDelete(null); }}
        onConfirm={handleDeleteConfirm}
        confirmText="Delete"
      >
        <p style={{ color: '#ccc' }}>Are you sure you want to delete this template? This cannot be undone.</p>
      </Modal>

      {/* Feedback */}
      <ModalNotification
        show={feedback.show}
        message={feedback.message}
        success={feedback.success}
        onComplete={() => setFeedback(prev => ({ ...prev, show: false }))}
      />
    </DashboardLayout>
  );
};

export default TemplateBuilderPage;
